import { Attachment, ModuleLayout, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { DuplicatedReceiptsModal } from '@features/attachments/elements/DuplicatedReceiptsModal'

import { ModalId } from '../../../../enums/modalId'
import { Scope } from '../../../../enums/scope'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { BulkAttachmentsContextProvider } from '../../../bills/contexts/bulkAttachmentsContext'
import { useDuplicateAttachments } from '../../../receipts/hooks/useDuplicateAttachments'
import { BillAttachmentPageContextProvider } from '../../contexts/billAttachmentPageContext'
import { BillEditableActions } from '../BillEditableActions'
import { BillFormContextProvider } from '../BillEditableForm'
import type { BillFormPartial } from '../BillEditableForm/utils/formData'
import { BillEditableLayout } from '../BillEditableLayout'
import { BillEditableTitle } from '../BillEditableTitle'

interface BillEditableProps {
  attachmentId?: string
  billId?: string
  billIdToCreateCreditNote?: string
  billIdToDuplicate?: string
  contactId?: string
  initialFormValues?: BillFormPartial
  isBulkEditMode?: boolean
  isNewCreditNote?: boolean
  modalDisplayMode?: boolean
  onBackButtonClick?: () => void
}

export const BillEditable = ({
  attachmentId,
  billId,
  billIdToCreateCreditNote,
  billIdToDuplicate,
  contactId,
  initialFormValues,
  isBulkEditMode,
  isNewCreditNote,
  modalDisplayMode,
  onBackButtonClick,
}: BillEditableProps): ReactElement => {
  const location = useLocation()
  const navigate = useNavigate()
  const { url: billsUrl } = useOrganizationViewUrl(reactRoute.organization.bills.getMainRoute())
  const { duplicates } = useDuplicateAttachments()
  const [selectedDuplicates, setSelectedDuplicates] = useState<Attachment[] | null>(null)
  const { open: openDuplicateModal } = useModal(ModalId.DuplicatedReceiptsActionModal)

  const handleOpenDuplicate = useCallback(() => {
    const matched = attachmentId ? duplicates.find((d) => d.id === attachmentId) : null
    setSelectedDuplicates(matched ? [matched] : null)
    openDuplicateModal()
    navigate(-1)
  }, [attachmentId, duplicates, navigate, openDuplicateModal])

  const showBillActions = !modalDisplayMode && isAuthorized(Scope.BillWrite)

  return (
    <BulkAttachmentsContextProvider>
      <BillAttachmentPageContextProvider>
        <BillFormContextProvider
          attachmentId={attachmentId}
          billId={billId}
          billIdToCreateCreditNote={billIdToCreateCreditNote}
          billIdToDuplicate={billIdToDuplicate}
          contactId={contactId}
          initialFormValues={initialFormValues}
          isBulkEditMode={isBulkEditMode}
          isNewCreditNote={isNewCreditNote}
          onOpenDuplicate={handleOpenDuplicate}
          modalDisplayMode={modalDisplayMode}
        >
          <ModuleLayout
            fullHeight
            backButtonTo={!modalDisplayMode ? billsUrl && `${billsUrl}${location.search}` : undefined}
            onBackButtonClick={onBackButtonClick}
            sideActions={showBillActions ? <BillEditableActions /> : null}
            title={<BillEditableTitle />}
            withBackButton={!!billId || modalDisplayMode}
          >
            <BillEditableLayout />
          </ModuleLayout>
          <DuplicatedReceiptsModal
            id={ModalId.DuplicatedReceiptsActionModal}
            duplicates={selectedDuplicates ?? duplicates}
          />
        </BillFormContextProvider>
      </BillAttachmentPageContextProvider>
    </BulkAttachmentsContextProvider>
  )
}
